import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Container, Table } from "reactstrap";

import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import { adminThemes } from "data/admin/domain";
import { provincesRepo } from "data/repositories/provincesRepo";
import { alertsRepo } from "data/repositories/alertsRepo";

const sortOptions = [
  { value: "created-desc", label: "Yeni eklenen" },
  { value: "province-asc", label: "İle göre" },
];

export default function AdminAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [sortBy, setSortBy] = useState("created-desc");

  useEffect(() => {
    try {
      setAlerts(alertsRepo.list());
    } catch (loadError) {
      setError(loadError?.message || "Alert kayıtları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  const provinceOptions = useMemo(
    () => [{ value: "", label: "Tüm iller" }, ...provincesRepo.list().map((province) => ({ value: province.id, label: province.name }))],
    []
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = alerts.filter((alert) => {
      if (provinceFilter && alert.provinceId !== provinceFilter) return false;
      if (themeFilter && alert.theme !== themeFilter) return false;
      if (!query) return true;
      return [alert.provinceId, alert.theme, alert.dateFrom, alert.dateTo, alert.title].join(" ").toLowerCase().includes(query);
    });

    return [...next].sort((left, right) => {
      if (sortBy === "province-asc") return String(left.provinceId || "").localeCompare(String(right.provinceId || ""), "tr");
      return String(right.createdAt || "").localeCompare(String(left.createdAt || ""));
    });
  }, [alerts, provinceFilter, search, sortBy, themeFilter]);

  const resetFilters = () => {
    setSearch("");
    setProvinceFilter("");
    setThemeFilter("");
    setSortBy("created-desc");
  };

  return (
    <>
      <AdminPageHeader title="Admin — Uyarı Yönetimi" description="Kalıcı alert rule kayıtları admin tarafından görünür; bu ekran read/write yerine yönetim görünürlüğüne odaklanır." />
      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Kalıcı alert rule kayıtları</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="İl, tema veya tarih ara"
              filters={[
                { key: "province", label: "İl", value: provinceFilter, onChange: setProvinceFilter, options: provinceOptions },
                { key: "theme", label: "Tema", value: themeFilter, onChange: setThemeFilter, options: [{ value: "", label: "Tüm temalar" }, ...adminThemes.map((theme) => ({ value: theme, label: theme }))] },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={alerts.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={!filtered.length}
              emptyTitle="Alert kaydı yok"
            >
              <Table responsive>
                <thead className="thead-light">
                  <tr>
                    <th>ID</th>
                    <th>İl</th>
                    <th>Tema</th>
                    <th>Tarih aralığı</th>
                    <th>Oluşturulma</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((alert) => (
                    <tr key={alert.id}>
                      <td><small>{alert.id}</small></td>
                      <td>{alert.provinceId || "Tümü"}</td>
                      <td>{alert.theme ? <Badge color="info" pill>{alert.theme}</Badge> : "Tümü"}</td>
                      <td>{alert.dateFrom || "-"} / {alert.dateTo || "-"}</td>
                      <td>{alert.createdAt || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}